import { Feature, FeatureCollection, Point } from "geojson";
import MapboxGl, { Expression, LngLatLike, StyleFunction } from "mapbox-gl";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import { FeatureProperties, mapboxKey } from "~/Dashboard/MapBox";
import * as Data from "~/Data";
import { Device } from "~/Device";
import * as Driver from "~/Driver";
import { Mutable } from "~/Mutable";
import { Version } from "~/Version";

import * as Popup from "./Popup";

export const Component: Component = () =>
  pipe(
    Driver.useStore(),
    Data.Store.toCache,
    Data.Cache.reduce(
      [] as Mutable<readonly Feature[]>,
      (_id, data, { id, status, location, name, device }) => {
        if (location && location.gps?.latitude && location.gps?.longitude) {
          const appVersion = device?.application?.version
            ? Version.toString({ version: device?.application?.version })
            : "Unknown";

          const osType = device?.platform
            ? Device.Platform.toString({ platform: device.platform })
            : "Unknown";

          const osVersion = device?.platform
            ? Version.toString(
                Device.Platform.toVersion({ platform: device.platform })
              )
            : "Unknown";

          // eslint-disable-next-line functional/no-expression-statement,functional/immutable-data
          data.push({
            type: "Feature",
            id: id,
            geometry: {
              type: "Point",
              coordinates: [location.gps.longitude, location.gps.latitude],
            },
            properties: {
              id,
              name: Driver.Name.toString(name),
              status: status ?? "OFF",
              appVersion,
              osType,
              osVersion,
            } as FeatureProperties,
          });
        }
        return data;
      }
    ),
    (features) =>
      ({
        type: "FeatureCollection",
        features,
      } as FeatureCollection),
    (data) =>
      data && data.features.length > 0 ? (
        <Map.Component featureCollection={data} />
      ) : null
  );

export namespace MapContainer {
  export const Component = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;

    & .mapboxgl-popup {
      filter: drop-shadow(0 0 1.5rem ${Color.steel()});

      & .cluster-chart {
        height: 250px;
        width: 230px;
      }

      & .cluster-popup {
        min-height: 250px;
        min-width: 290px;
      }

      z-index: 1000;
    }

    & .mapboxgl-popup-content {
      border-radius: 0.6em;
    }

    & .driver-popup {
      border-radius: 1.25em;
      > span {
        text-align: center;
      }
      z-index: 1000;
    }

    & .map-container {
      background-color: ${Color.azure()};
      & .cluster-popup > div {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        justify-items: baseline;
      }
    }
  `;
}

export namespace Map {
  export type Props = {
    readonly featureCollection: FeatureCollection;
  };

  export const Component: Component<Props> = ({ featureCollection }) => {
    const mapContainerRef = useRef(null);

    useEffect(() => {
      const map = new MapboxGl.Map({
        accessToken: mapboxKey,
        container: mapContainerRef.current ?? "",
        style: "mapbox://styles/mapbox/streets-v11",
        center: [-98.5795, 39.8283], // Roughly the center of the United States
        zoom: 4,
      });

      map.on("load", () => {
        // eslint-disable-next-line functional/no-expression-statement
        Layer.buildDriverLayerSource(map, featureCollection);

        // eslint-disable-next-line functional/no-expression-statement
        Layer.buildDriverLayers(map);

        const popup = new MapboxGl.Popup({
          closeButton: false,
          closeOnClick: false,
        });

        // eslint-disable-next-line functional/no-expression-statement
        Popup.mapPopup(map, popup, "drivers", Popup.driverPopupHandler);

        // eslint-disable-next-line functional/no-expression-statement
        Popup.mapPopup(
          map,
          popup,
          "clustered_drivers_center",
          Popup.clusterPopupHandler
        );
      });
      // clean up on unmount
      return () => map.remove();
    }, []);

    return (
      <MapContainer.Component className="map-container" ref={mapContainerRef} />
    );
  };
}

export namespace Layer {
  export type RuleExpression = number | StyleFunction | Expression;

  const driverFilter: RuleExpression = ["==", ["get", "status"], "D"];
  const onFilter: RuleExpression = ["==", ["get", "status"], "ON"];
  const offFilter: RuleExpression = ["==", ["get", "status"], "OFF"];
  const sbFilter: RuleExpression = ["==", ["get", "status"], "SB"];
  const ymFilter: RuleExpression = ["==", ["get", "status"], "YM"];
  const puFilter: RuleExpression = ["==", ["get", "status"], "PU"];

  const blueFilter: RuleExpression = [
    "in",
    ["get", "status"],
    ["literal", ["D", "YM"]],
  ];

  const grayFilter: RuleExpression = [
    "in",
    ["get", "status"],
    ["literal", ["SB", "OFF", "PU"]],
  ];

  const minimumClusterSize = (): number => 12;
  const maximumClusterSize = (): number => 40;

  export const addDriverClusterLayer = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map: any,
    layerName: string,
    color: Color.Color,
    countName: string,
    offset: RuleExpression,
    strokeWidth: RuleExpression
  ) => {
    // eslint-disable-next-line functional/no-expression-statement
    map.addLayer({
      id: layerName,
      source: "drivers-source",
      type: "circle",
      filter: ["has", countName],
      paint: {
        "circle-radius": [
          "+",
          ["-", offset, strokeWidth],
          minimumClusterSize(),
        ],
        "circle-color": Color.mustard(),
        "circle-opacity": 0.3,
        "circle-stroke-color": color,
        "circle-stroke-width": strokeWidth,
        "circle-stroke-opacity": 0.5,
      },
    });
  };

  export const buildDriverLayerSource = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map: any,
    featureCollection: FeatureCollection
  ) => {
    // eslint-disable-next-line functional/no-expression-statement
    map.addSource("drivers-source", {
      type: "geojson",
      data: featureCollection,
      cluster: true,
      clusterRadius: 40,
      clusterProperties: {
        // keep separate counts for each status category in a cluster
        driveCount: ["+", ["case", driverFilter, 1, 0]],
        onCount: ["+", ["case", onFilter, 1, 0]],
        offCount: ["+", ["case", offFilter, 1, 0]],
        sbCount: ["+", ["case", sbFilter, 1, 0]],
        puCount: ["+", ["case", puFilter, 1, 0]],
        ymCount: ["+", ["case", ymFilter, 1, 0]],
        blueCount: ["+", ["case", blueFilter, 1, 0]],
        grayCount: ["+", ["case", grayFilter, 1, 0]],
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export const buildDriverLayers = (map: any) => {
    const minRadiusRule = [
      "min",
      ["max", ["get", "point_count"], minimumClusterSize()],
      maximumClusterSize(),
    ];

    const getCount = (countName: string): RuleExpression => ["get", countName];

    const countGreaterThen = (
      countName: string,
      value: RuleExpression
    ): RuleExpression => [">", getCount(countName), value];

    const countEqual = (
      countName: string,
      value: RuleExpression
    ): RuleExpression => ["==", getCount(countName), value];

    const hasCount = (countName: string): RuleExpression =>
      countGreaterThen(countName, 0);

    const offsetIfCount = (countName: string): RuleExpression => [
      "case",
      hasCount(countName),
      5,
      0,
    ];

    const grayCountAdditionRule: RuleExpression = offsetIfCount("grayCount");

    const greenCountAdditionRule: RuleExpression = [
      "+",
      offsetIfCount("grayCount"),
      offsetIfCount("onCount"),
    ];

    const blueCountAdditionRule: RuleExpression = [
      "+",
      offsetIfCount("grayCount"),
      offsetIfCount("onCount"),
      offsetIfCount("blueCount"),
    ];

    const plusMinRadiusRule = (rule: RuleExpression): RuleExpression => [
      "+",
      minRadiusRule,
      rule,
    ];

    const countCompare = (count1: string, count2: string): RuleExpression =>
      countGreaterThen(count1, getCount(count2));

    const countCompareEqual = (
      count1: string,
      count2: string
    ): RuleExpression => [
      "case",
      countGreaterThen(count1, 0),
      countGreaterThen(count2, 0),
      countEqual(count1, getCount(count2)),
    ];

    const greenColorWeightRule = (nextRule: RuleExpression): RuleExpression => [
      "case",
      countCompare("onCount", "grayCount"),
      Color.green(),
      countCompare("onCount", "blueCount"),
      Color.green(),
      nextRule,
    ];

    const grayColorWeightRule = (nextRule: RuleExpression): RuleExpression => [
      "case",
      countCompare("grayCount", "blueCount"),
      Color.steel(),
      countCompare("grayCount", "onCount"),
      Color.steel(),
      nextRule,
    ];

    const blueColorWeightRule = (nextRule: RuleExpression): RuleExpression => [
      "case",
      countCompare("blueCount", "onCount"),
      Color.azure(),
      countCompare("blueCount", "grayCount"),
      Color.azure(),
      nextRule,
    ];

    const colorWeightRule: RuleExpression = greenColorWeightRule(
      grayColorWeightRule(
        blueColorWeightRule([
          "case",
          countCompareEqual("onCount", "blueCount"),
          Color.green(),
          [
            "case",
            countCompareEqual("blueCount", "grayCount"),
            Color.azure(),
            Color.steel(),
          ],
        ])
      )
    );

    // eslint-disable-next-line functional/no-expression-statement
    map.addLayer({
      id: "clustered_drivers_base",
      source: "drivers-source",
      type: "circle",
      filter: ["has", "point_count"],
      paint: {
        "circle-radius": plusMinRadiusRule(blueCountAdditionRule),
        "circle-blur": 0.3,
        "circle-color": Color.white(),
        "circle-opacity": 0.9,
        "circle-stroke-width": 6,
        "circle-stroke-opacity": 0.7,
        "circle-stroke-color": colorWeightRule,
      },
    });

    // eslint-disable-next-line functional/no-expression-statement
    map.addLayer({
      id: "drivers_circle_gray",
      source: "drivers-source",
      type: "circle",
      filter: hasCount("grayCount"),
      paint: {
        "circle-radius": plusMinRadiusRule(2),
        "circle-opacity": 0,
        "circle-stroke-color": Color.steel(),
        "circle-stroke-width": 3,
      },
    });

    // eslint-disable-next-line functional/no-expression-statement
    map.addLayer({
      id: "drivers_circle_green",
      source: "drivers-source",
      type: "circle",
      filter: hasCount("onCount"),
      paint: {
        "circle-radius": plusMinRadiusRule(["+", 2, grayCountAdditionRule]),
        "circle-opacity": 0,
        "circle-stroke-color": Color.green(),
        "circle-stroke-width": 3,
      },
    });

    // eslint-disable-next-line functional/no-expression-statement
    map.addLayer({
      id: "drivers_circle_blue",
      source: "drivers-source",
      type: "circle",
      filter: hasCount("blueCount"),
      paint: {
        "circle-radius": plusMinRadiusRule(["+", 2, greenCountAdditionRule]),
        "circle-opacity": 0,
        "circle-stroke-color": Color.azure(),
        "circle-stroke-width": 3,
      },
    });

    // eslint-disable-next-line functional/no-expression-statement
    map.addLayer({
      id: "clustered_drivers_center",
      source: "drivers-source",
      type: "circle",
      filter: ["has", "point_count"],
      paint: {
        "circle-radius": minRadiusRule,
        "circle-color": Color.white(),
        "circle-opacity": 1,
        "circle-stroke-width": 0,
      },
    });

    // eslint-disable-next-line functional/no-expression-statement
    map.addLayer({
      id: "clustered_drivers_label",
      type: "symbol",
      source: "drivers-source",
      filter: ["==", "cluster", true],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-size": 12,
      },
      paint: {
        "text-color": Color.steel(),
      },
    });

    // Add a layer to represent the non-clustered driver markers.
    // eslint-disable-next-line functional/no-expression-statement
    map.addLayer({
      id: "drivers",
      source: "drivers-source",
      type: "circle",
      filter: ["!=", "cluster", true],
      paint: {
        "circle-radius": 13,
        "circle-color": [
          "case",
          driverFilter,
          Driver.Status.toColor("D"),
          onFilter,
          Driver.Status.toColor("ON"),
          offFilter,
          Driver.Status.toColor("OFF"),
          sbFilter,
          Driver.Status.toColor("SB"),
          ymFilter,
          Driver.Status.toColor("YM"),
          puFilter,
          Driver.Status.toColor("PU"),
          /* other */ Color.mustard(),
        ],
        "circle-opacity": 0.9,
      },
    });

    // eslint-disable-next-line functional/no-expression-statement
    map.addLayer({
      id: "drivers_label",
      type: "symbol",
      source: "drivers-source",
      filter: ["!=", "cluster", true],
      layout: {
        "text-field": ["string", ["get", "status"]],
        "text-size": 10,
      },
      paint: {
        "text-color": Color.black(),
      },
    });
  };
}

export namespace Source {
  export type MapMarkerData = {
    readonly latitude: number;
    readonly longitude: number;
    readonly status: Driver.Status.Status;
    readonly key: string;
  };

  export const featureCollection = () => {
    const useMapMarkerData = (): FeatureCollection =>
      pipe(
        Driver.useStore(),
        Data.Store.toCache,
        Data.Cache.reduce(
          // eslint-disable-next-line functional/prefer-readonly-type
          [] as Feature[],
          (_id, data, { id, status, location, device, name }) => {
            if (location && location.gps?.latitude && location.gps?.longitude) {
              // eslint-disable-next-line functional/no-expression-statement,functional/immutable-data
              data.push({
                type: "Feature",
                id: id,
                geometry: {
                  type: "Point",
                  coordinates: [location.gps.longitude, location.gps.latitude],
                },
                properties: {
                  id,
                  name: Driver.Name.toString(name),
                  status: status ?? "OFF",
                  device,
                },
              });
            }
            return data;
          }
        ),
        (features) => ({
          type: "FeatureCollection",
          features,
        })
      );

    return useMapMarkerData();
  };

  export const toBounds = (
    sw: Feature,
    ne: Feature
  ): readonly [LngLatLike, LngLatLike] => [toCoordinate(sw), toCoordinate(ne)];

  export const toCoordinate = (feature: Feature): LngLatLike =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion,functional/prefer-readonly-type
    (feature.geometry as Point).coordinates as [number, number];
}
